import { hex, mix, shade, W, type Col } from "../art/pal.ts";
import { hash2, vnoise } from "./noise.ts";
import { Spr } from "./spr.ts";

/**
 * Procedural world props. Every sprite is painted once, cached, and anchored at its base centre
 * (`ax`, `ay` = the pixel that sits on the ground line) so the world can depth-sort by base y.
 */
export interface PropSprite {
  img: ImageData;
  ax: number;
  ay: number;
}

const cache = new Map<string, PropSprite>();
function cached(key: string, make: () => PropSprite): PropSprite {
  let hit = cache.get(key);
  if (!hit) {
    hit = make();
    cache.set(key, hit);
  }
  return hit;
}

const INK = hex("#1a1430");

/** Round broadleaf tree: clustered lit canopy lobes over a short trunk. */
export function treeSprite(variant: number, kind: "round" | "blossom" | "autumn" = "round"): PropSprite {
  return cached(`tree:${kind}:${variant}`, () => {
    const w = 36, h = 44;
    const s = new Spr(w, h);
    const bark = W.bark;
    const leaf: readonly Col[] =
      kind === "blossom"
        ? [hex("#ffe1ec"), hex("#ffb3cc"), hex("#f28bb0"), hex("#d0668f"), hex("#9c4a78"), hex("#6a3160")]
        : kind === "autumn"
          ? [hex("#ffe08a"), hex("#ffb85a"), hex("#ef8a3f"), hex("#c9602f"), hex("#8f3d2c"), hex("#5c2828")]
          : W.leaf;
    // Trunk with root flare.
    const tx = 18;
    s.fill(tx - 3, 28, 6, 13, bark[2]!);
    s.fill(tx - 3, 28, 2, 13, bark[1]!);
    s.fill(tx + 2, 28, 1, 13, bark[3]!);
    s.p(tx - 4, 40, bark[2]!);
    s.p(tx + 3, 40, bark[3]!);
    s.p(tx - 5, 41, bark[3]!);
    s.p(tx + 4, 41, bark[3]!);
    s.hline(tx - 4, 41, 8, bark[3]!);
    // Canopy: a big base blob plus lobes, each shaded as a volume.
    const r = (n: number) => hash2(variant, n, 91);
    const lobes: [number, number, number, number][] = [
      [18, 20, 15, 12],
      [11 + r(1) * 2, 17 + r(2) * 2, 8, 7],
      [25 - r(3) * 2, 16 + r(4) * 2, 8, 7],
      [17 + r(5) * 3, 10 + r(6) * 2, 9, 7],
      [9 + r(7) * 2, 24, 7, 6],
      [27 - r(8) * 2, 24, 7, 6],
    ];
    s.ball(18, 21, 16, 13, leaf, { bias: 0.9, flat: 0.3 });
    for (const [cx, cy, rx, ry] of lobes) s.ball(cx, cy, rx, ry, leaf, { bias: 0.35, rough: 0.35, seed: variant });
    // Leaf clusters: small highlight crescents on the lit side.
    for (let i = 0; i < 14; i++) {
      const lx = Math.round(6 + r(20 + i) * 24);
      const ly = Math.round(6 + r(40 + i) * 22);
      if (s.alpha(lx, ly) === 0) continue;
      const lit = lx + ly < 34;
      s.over(lx, ly, lit ? leaf[0]! : leaf[3]!);
      s.over(lx + 1, ly, lit ? leaf[1]! : leaf[4]!);
      s.over(lx, ly + 1, lit ? leaf[2]! : leaf[5]!);
    }
    if (kind === "round" && variant % 3 === 0) {
      // A few fruit dots.
      for (let i = 0; i < 4; i++) {
        const fx = Math.round(9 + r(70 + i) * 18), fy = Math.round(12 + r(80 + i) * 14);
        if (s.alpha(fx, fy)) { s.p(fx, fy, hex("#ff5a4f")); s.p(fx, fy - 1, hex("#ffb3a0")); }
      }
    }
    s.outline(INK, 0.4);
    return { img: s.img, ax: 18, ay: 41 };
  });
}

/** Layered pine. */
export function pineSprite(variant: number): PropSprite {
  return cached(`pine:${variant}`, () => {
    const w = 30, h = 46;
    const s = new Spr(w, h);
    const bark = W.bark;
    const ramp = W.pine;
    s.fill(13, 36, 4, 8, bark[2]!);
    s.fill(13, 36, 1, 8, bark[1]!);
    s.hline(12, 43, 6, bark[3]!);
    const tiers = 4;
    for (let t = tiers - 1; t >= 0; t--) {
      const top = 3 + t * 8;
      const bot = top + 14;
      const half = 5 + t * 2.6 + hash2(variant, t, 5) * 1.5;
      s.poly([[15, top], [15 + half, bot], [15 - half, bot]], (x, y) => {
        const nx = (x - 15) / half, ny = (y - top) / (bot - top);
        const lam = -nx * 0.8 + (0.4 - ny) * 0.9 + (hash2(x, y, 9) - 0.5) * 0.3;
        return Spr.tone(ramp, Math.max(-1, Math.min(1, lam)), x, y, 0.3);
      });
      // Bough tips catch light.
      for (let x = Math.ceil(15 - half); x < 15 + half; x += 3) {
        if (x < 15) s.over(x, bot - 1, ramp[1]!);
      }
    }
    s.outline(INK, 0.38);
    return { img: s.img, ax: 15, ay: 43 };
  });
}

/** Boulder. */
export function boulderSprite(variant: number): PropSprite {
  return cached(`boulder:${variant}`, () => {
    const s = new Spr(20, 18);
    const ramp = W.rock;
    s.ball(10, 10, 8.5, 7, ramp, { rough: 0.5, seed: variant, bias: 0.2, flat: 0.2 });
    if (variant % 2) s.ball(14, 12, 4, 3.5, ramp, { rough: 0.4, seed: variant + 3, bias: 0.5 });
    // Cracks and moss.
    for (let i = 0; i < 3; i++) {
      const cx = 5 + Math.round(hash2(variant, i, 3) * 10), cy = 6 + Math.round(hash2(variant, i, 4) * 6);
      s.over(cx, cy, ramp[4]!);
      s.over(cx + 1, cy + 1, ramp[4]!);
    }
    for (let x = 3; x < 16; x++) if (hash2(x, variant, 8) < 0.35) s.over(x, 5 + Math.round(vnoise(x / 3, variant) * 2), W.grass[2]!);
    s.outline(INK, 0.4);
    return { img: s.img, ax: 10, ay: 16 };
  });
}

/** Street lamp: iron post with a glass lantern head. */
export function lampSprite(): PropSprite {
  return cached("lamp", () => {
    const s = new Spr(12, 36);
    const iron = [hex("#6b7394"), hex("#474e6d"), hex("#2c3048"), hex("#1b1d2e")];
    s.fill(5, 10, 2, 23, iron[1]!);
    s.vline(5, 10, 23, iron[0]!);
    s.fill(3, 31, 6, 3, iron[2]!);
    s.hline(3, 31, 6, iron[1]!);
    s.fill(4, 29, 4, 2, iron[1]!);
    // Lantern head.
    s.fill(2, 2, 8, 2, iron[2]!);
    s.fill(3, 1, 6, 1, iron[1]!);
    s.fill(3, 4, 6, 6, hex("#ffe7a0"));
    s.fill(4, 5, 4, 4, hex("#fff8d8"));
    s.vline(3, 4, 6, iron[2]!);
    s.vline(8, 4, 6, iron[2]!);
    s.fill(2, 10, 8, 1, iron[2]!);
    s.p(5, 0, iron[1]!);
    s.p(6, 0, iron[1]!);
    s.outline(INK, 0.35);
    return { img: s.img, ax: 6, ay: 33 };
  });
}

export function crateSprite(variant: number): PropSprite {
  return cached(`crate:${variant}`, () => {
    const s = new Spr(18, 20);
    const wd = W.wood;
    // Front face and lid in 3/4 view.
    s.fill(1, 6, 16, 12, wd[1]!);
    s.fill(1, 2, 16, 5, wd[0]!);
    s.hline(1, 6, 16, wd[2]!);
    for (let y = 9; y < 18; y += 4) s.hline(1, y, 16, wd[2]!);
    s.vline(1, 6, 12, wd[3]!);
    s.vline(16, 6, 12, wd[3]!);
    s.line(2, 7, 15, 17, wd[2]!);
    s.hline(2, 3, 14, hex("#ffe0b0"));
    if (variant % 2) {
      // Produce peeking out: melons / tins.
      s.ball(6, 3, 3, 2, W.leaf, { bias: 0.2 });
      s.ball(11, 3, 3, 2, [hex("#ffd4d4"), hex("#ff7a7a"), hex("#d24b5a"), hex("#8f2f45")], {});
    }
    s.outline(INK, 0.4);
    return { img: s.img, ax: 9, ay: 18 };
  });
}

export function barrelSprite(): PropSprite {
  return cached("barrel", () => {
    const s = new Spr(16, 20);
    const wd = W.wood;
    for (let y = 3; y < 18; y++) {
      for (let x = 2; x < 14; x++) {
        const nx = (x - 7.5) / 6;
        const lam = -nx * 0.9 + 0.1;
        s.p(x, y, Spr.tone(wd, lam, x, y, 0.4));
      }
    }
    s.oval(8, 3, 6, 2, wd[0]!);
    s.oval(8, 3, 4, 1, wd[2]!);
    for (const y of [6, 14]) s.hline(2, y, 12, hex("#474e6d"));
    s.outline(INK, 0.4);
    return { img: s.img, ax: 8, ay: 18 };
  });
}

export function fenceSprite(left: boolean, right: boolean): PropSprite {
  return cached(`fence:${left}:${right}`, () => {
    const s = new Spr(16, 18);
    const wd = W.wood;
    for (const x of [2, 13]) {
      s.fill(x, 3, 2, 13, wd[1]!);
      s.vline(x, 3, 13, wd[0]!);
      s.p(x, 2, wd[0]!);
    }
    s.fill(left ? 0 : 2, 6, (right ? 16 : 15) - (left ? 0 : 2), 2, wd[1]!);
    s.fill(left ? 0 : 2, 11, (right ? 16 : 15) - (left ? 0 : 2), 2, wd[1]!);
    s.hline(left ? 0 : 2, 6, (right ? 16 : 15) - (left ? 0 : 2), wd[0]!);
    s.hline(left ? 0 : 2, 11, (right ? 16 : 15) - (left ? 0 : 2), wd[0]!);
    s.outline(INK, 0.4);
    return { img: s.img, ax: 8, ay: 16 };
  });
}

export function signSprite(): PropSprite {
  return cached("sign", () => {
    const s = new Spr(18, 22);
    const wd = W.wood;
    s.fill(8, 10, 2, 11, wd[2]!);
    s.vline(8, 10, 11, wd[1]!);
    s.fill(1, 2, 16, 10, wd[1]!);
    s.hline(1, 2, 16, wd[0]!);
    s.hline(1, 11, 16, wd[3]!);
    s.fill(3, 4, 12, 6, hex("#f6e3bd"));
    for (const y of [5, 7]) s.hline(4, y, 9 - (y === 7 ? 3 : 0), hex("#8a6a50"));
    s.p(2, 3, hex("#ffd84a"));
    s.p(15, 3, hex("#ffd84a"));
    s.outline(INK, 0.4);
    return { img: s.img, ax: 9, ay: 20 };
  });
}

/** Reed / cattail cluster standing in shallow water (4 sway frames). */
export function reedSprite(variant: number, frame: number): PropSprite {
  return cached(`reed:${variant}:${frame}`, () => {
    const s = new Spr(18, 24);
    const stems = 5;
    for (let i = 0; i < stems; i++) {
      const bx = 2 + i * 3 + Math.round(hash2(variant, i, 2) * 2);
      const hgt = 12 + Math.round(hash2(variant, i, 3) * 8);
      const sway = Math.round(Math.sin((frame / 4) * Math.PI * 2 + i * 0.9) * 1.2);
      for (let y = 0; y < hgt; y++) {
        const t = y / hgt;
        const x = bx + Math.round(sway * t * t);
        s.p(x, 22 - y, y < 3 ? W.grass[4]! : t > 0.7 ? W.grass[1]! : W.grass[2]!);
      }
      if (i % 2 === 0) {
        const tx = bx + sway;
        s.fill(tx, 22 - hgt - 1, 2, 4, hex("#8a5a3a"));
        s.p(tx, 22 - hgt - 1, hex("#b07a4f"));
      }
    }
    s.outline(INK, 0.5);
    return { img: s.img, ax: 9, ay: 22 };
  });
}

/** Kettle hearth: a warm stone ring with a little kettle (rest pads). */
export function kettleSprite(): PropSprite {
  return cached("kettle", () => {
    const s = new Spr(18, 16);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      s.ball(9 + Math.cos(a) * 6, 11 + Math.sin(a) * 3, 2, 1.6, W.rock, { bias: 0.2 });
    }
    s.oval(9, 11, 3, 1.5, hex("#ff9a3c"));
    s.p(9, 11, hex("#fff0a8"));
    // Kettle.
    const metal = [hex("#e4f4ff"), hex("#9fc4e8"), hex("#5d7fb0"), hex("#344a78")];
    s.ball(9, 6, 4, 3.5, metal, { bias: 0.1 });
    s.fill(8, 2, 2, 1, metal[2]!);
    s.line(13, 5, 15, 3, metal[2]!);
    s.p(5, 5, metal[2]!);
    s.p(4, 6, metal[2]!);
    s.outline(INK, 0.4);
    return { img: s.img, ax: 9, ay: 14 };
  });
}

/**
 * Tall-grass tuft covering one tile. `frame` 0..3 sways the tips; `rustle` bends harder.
 * The sprite is 18×20 anchored at the tile's bottom centre so it overlaps neighbours a little.
 */
export function tallTuft(variant: number, frame: number, rustle = false): PropSprite {
  return cached(`tall:${variant}:${frame}:${rustle}`, () => {
    const s = new Spr(20, 24);
    const g = W.grass;
    const blades = 10;
    for (let i = 0; i < blades; i++) {
      const bx = 1 + Math.round((i / (blades - 1)) * 17) + Math.round((hash2(variant, i, 1) - 0.5) * 2);
      const hgt = 9 + Math.round(hash2(variant, i, 2) * 11);
      const lean = (hash2(variant, i, 4) - 0.5) * 3;
      const sway = Math.sin((frame / 4) * Math.PI * 2 + bx * 0.35) * (rustle ? 3 : 1.4);
      for (let y = 0; y < hgt; y++) {
        const t = y / hgt;
        const x = bx + Math.round((lean + sway) * t * t);
        const c = t < 0.25 ? g[5]! : t < 0.55 ? g[3]! : t < 0.85 ? g[2]! : g[1]!;
        s.p(x, 20 - y, c);
        if (t < 0.6) s.p(x + 1, 20 - y, t < 0.3 ? g[5]! : g[4]!);
      }
      if (hash2(variant, i, 7) < 0.3) s.p(bx + Math.round(lean + sway), 20 - hgt, g[0]!);
    }
    // Dense, ragged base.
    for (let x = 0; x < 20; x++) {
      const top = 16 + Math.round(hash2(x, variant, 9) * 3);
      for (let y = top; y < 21; y++) if (hash2(x, y, variant) < 0.85) s.p(x, y, y > 19 ? g[6]! : y > 17 ? g[5]! : g[4]!);
    }
    s.outline(hex("#132a24"), 0.55);
    return { img: s.img, ax: 10, ay: 20 };
  });
}

/** Little flower decal for baked ground. */
export function flowerColors(n: number): { petal: Col; core: Col } {
  const petals = W.flowers;
  const petal = petals[n % petals.length]!;
  return { petal, core: n % 3 === 0 ? hex("#fff0a8") : shade(petal, 0.7) };
}

export function mixCol(a: Col, b: Col, t: number): Col {
  return mix(a, b, t);
}
